/* eslint-disable camelcase */
import { Grid, Tooltip } from '@mui/material';
import { pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { RoutalAvatar, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import More3Points from '../../../../assets/svg/More3Points';
import { HeaderContent } from '../../../../components/HeaderContent';
import { PLAN_TYPE } from '../../../../constants/Subscriptions';
import { EventType } from '../../../../domain/webhook/EventType';
import { Webhook } from '../../../../domain/webhook/Webhook';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveFlatProjects } from '../../../../hooks/project/useRetrieveFlatProjects';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import { useRetrieveWebhooks } from '../../../../hooks/webhooks/useRetrieveWebhooks';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { DataTable } from '../../../../react-components/data-table/DataTable';
import { getUploadAsBase64 } from '../../../../services/requests';
import { UpgradeIntegrations } from '../common/UpgradeIntegrations';
import { CreateWebhookDialog } from './dialogs/CreateWebhookDialog';
import { DeleteWebhookDialog } from './dialogs/DeleteWebhookDialog';
import { TestWebhookDialog } from './dialogs/TestWebhookDialog';
import { UpdateWebhookDialog } from './dialogs/UpdateWebhookDialog';

const Webhooks = () => {
  const { data: user } = useRetrieveUser();
  const { data: organization } = useRetrieveOrganization();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projects } = useRetrieveFlatProjects({ user });
  const [projectId, setProjectId] = useState<string | undefined>(activeProject?.id);
  const { data: webhooks, run: retrieveWebhooks } = useRetrieveWebhooks(projectId);

  const [activeDialog, setActiveDialog] = useState<null | `create` | `update` | `test` | `delete`>(null);
  const [activeWebhook, setActiveWebhook] = useState<Webhook>();

  const closeWebhookDialog = () => {
    setActiveDialog(null);
    retrieveWebhooks();
  };

  useEffect(() => {
    if (activeProject && !projectId) {
      setProjectId(activeProject.id);
    }
  }, [activeProject, projectId]);

  const getCurrentProject = () => {
    const projectFound = projects?.find((project) => project.id === projectId);
    return projectFound;
  };

  if (organization?.subscription.plan === PLAN_TYPE.MONKEY) {
    return <UpgradeIntegrations />;
  }

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{
        padding: `16px 22px`,
        backgroundColor: `white`,
        zIndex: 2,
        height: `100%`,
        overflowY: `auto`,
      }}
    >
      <Grid container direction="column">
        <Grid container direction="column" style={{ padding: `16px 22px` }} wrap="nowrap">
          <Grid item container direction="row" justifyContent="space-between">
            <Grid item>
              <HeaderContent titleIntlId="settings.developers.webhooks.title" />
            </Grid>

            <Grid item>
              <Grid container direction="column" alignItems="flex-end">
                <Grid item>
                  <RoutalSelect
                    id="select-project"
                    popoverProps={{
                      options: [
                        ...(projects ?? []).map((project): RoutalSelectMultiSelectOption => {
                          return {
                            key: project.id,
                            width: 300,
                            onClick: (event: any) => {
                              setProjectId(project.id);
                            },
                            content: (
                              <Grid container alignItems="center" wrap="nowrap">
                                <RoutalAvatar
                                  url={project.avatar_url}
                                  getUploadAsBase64={getUploadAsBase64}
                                  label={project.label}
                                  labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                                  containerStyles={{ width: `24px`, height: `24px` }}
                                />
                                <Grid
                                  item
                                  style={{
                                    whiteSpace: `nowrap`,
                                    textOverflow: `ellipsis`,
                                    overflow: `hidden`,
                                    width: `252px`,
                                  }}
                                >
                                  <span style={{ marginLeft: `8px` }}>{project.label ?? ``}</span>
                                </Grid>
                              </Grid>
                            ),
                          };
                        }),
                      ],
                    }}
                  >
                    <Grid container alignItems="center" wrap="nowrap">
                      <RoutalAvatar
                        url={getCurrentProject()?.avatar_url}
                        getUploadAsBase64={getUploadAsBase64}
                        label={getCurrentProject()?.label}
                        labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                        containerStyles={{ width: `24px`, height: `24px` }}
                      />
                      <Grid
                        item
                        style={{
                          whiteSpace: `nowrap`,
                          textOverflow: `ellipsis`,
                          overflow: `hidden`,
                          width: `252px`,
                          textAlign: `start`,
                        }}
                      >
                        <span style={{ marginLeft: `8px` }}>{getCurrentProject()?.label}</span>
                      </Grid>
                    </Grid>
                  </RoutalSelect>
                </Grid>

                <VSpacer small />

                <Grid item>
                  <Button
                    style={{ width: `120px` }}
                    intlId="settings.developers.webhooks.create_new_webhook_button"
                    onClick={() => {
                      setActiveDialog(`create`);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <VSpacer />

          <DataTable
            emptyMessage={<Text intlId="settings.developers.webhooks.no_webhooks" />}
            docs={(webhooks ?? [])
              ?.sort((w1: Webhook, w2: Webhook) => (w1.created_at! < w2.created_at! ? 1 : -1))
              .map((webhook: Webhook) => {
                return {
                  doc: webhook,
                };
              })}
            showCheckboxes={false}
            total={webhooks?.length ?? 0}
            columns={[
              {
                accessorName: `url`,
                width: `calc(80% - 24px - 120px - 24px - 60px - 24px - 60px - 24px - 40px - 24px - 24px)`,
                name: <Text variant="label" intlId="settings.developers.webhooks.table.column.url" />,
                formatter: ({ doc }: { doc: Webhook }) => {
                  return <Text style={{ wordBreak: `break-all` }}>{doc.url}</Text>;
                },
              },
              {
                accessorName: `api_version`,
                width: `24px`,
                align: `center`,
                name: <Text variant="label" intlId="settings.developers.webhooks.table.column.version" />,
                formatter: ({ doc }: { doc: Webhook }) => doc.api_version,
              },
              {
                accessorName: `enabled_events`,
                width: `120px`,
                name: <Text variant="label" intlId="settings.developers.webhooks.table.column.events" />,
                formatter: ({ doc }: { doc: Webhook }) => (
                  <ul style={{ padding: `0` }}>
                    {doc.enabled_events
                      ? doc.enabled_events.map((event: EventType) => (
                          <li key={`event-${event}`}>
                            <Text intlId={`settings.developers.webhooks.events.${event}`} />
                          </li>
                        ))
                      : null}
                  </ul>
                ),
              },
              {
                accessorName: `enabled`,
                width: `60px`,
                name: <Text variant="label" intlId="settings.developers.webhooks.table.column.status" />,
                formatter: ({ doc }: { doc: Webhook }) => (
                  <>
                    {doc.enabled && doc.failure_count && doc.failure_count > 0 ? (
                      <Grid container alignItems="center">
                        <Text
                          intlId={`settings.developers.webhooks.table.column.${doc.enabled ? `enabled` : `disabled`}`}
                        />
                        <Tooltip
                          title={
                            <Text
                              intlId="settings.developers.webhooks.table.column.fail"
                              intlValues={{ failure_count: doc.failure_count }}
                            />
                          }
                        >
                          <div
                            style={{
                              padding: `6px`,
                              margin: 10,
                              backgroundColor: `red`,
                              borderRadius: `50%`,
                              width: 15,
                              height: 15,
                              display: `flex`,
                              justifyContent: `center`,
                              alignItems: `center`,
                            }}
                          >
                            <p style={{ color: `white`, fontWeight: `bold`, fontSize: `14px` }}>{doc.failure_count}</p>
                          </div>
                        </Tooltip>
                      </Grid>
                    ) : (
                      <Grid container alignItems="center">
                        <Text
                          intlId={`settings.developers.webhooks.table.column.${doc.enabled ? `enabled` : `disabled`}`}
                        />
                      </Grid>
                    )}
                  </>
                ),
              },
              {
                accessorName: `created_at`,
                width: `60px`,
                name: <Text variant="label" intlId="settings.developers.webhooks.table.column.created_at" />,
                formatter: ({ doc }: { doc: Webhook }) => pastDateTime(doc.created_at),
              },
              {
                accessorName: `actions`,
                align: `right`,
                width: `40px`,
                formatter: ({ doc }: { doc: Webhook }) => {
                  return (
                    <RoutalSelect
                      id="project-webhook-actions"
                      hideArrow
                      style={{
                        width: `28px`,
                        height: `28px`,
                        padding: `2px`,
                        margin: `0px`,
                        border: `none`,
                        backgroundColor: `transparent`,
                      }}
                      popoverProps={{
                        anchor: {
                          anchorOrigin: {
                            vertical: `bottom`,
                            horizontal: `right`,
                          },
                          transformOrigin: {
                            vertical: `top`,
                            horizontal: `right`,
                          },
                        },
                        options: [
                          {
                            key: `edit`,
                            onClick: () => {
                              setActiveWebhook(doc);
                              setActiveDialog(`update`);
                            },
                            content: (
                              <Text intlId="settings.developers.webhooks.actions.edit" style={{ cursor: `pointer` }} />
                            ),
                          },
                          {
                            key: `test_webhook`,
                            onClick: () => {
                              setActiveWebhook(doc);
                              setActiveDialog(`test`);
                            },
                            content: (
                              <Text intlId="settings.developers.webhooks.actions.test" style={{ cursor: `pointer` }} />
                            ),
                          },
                          {
                            key: `delete`,
                            onClick: () => {
                              setActiveWebhook(doc);
                              setActiveDialog(`delete`);
                            },
                            content: (
                              <Text
                                intlId="settings.developers.webhooks.actions.delete"
                                style={{ color: RoutalPalette.danger, cursor: `pointer` }}
                              />
                            ),
                          },
                        ],
                      }}
                    >
                      <More3Points />
                    </RoutalSelect>
                  );
                },
              },
            ]}
          />

          <VSpacer />
        </Grid>
        {projectId && activeDialog && (
          <>
            {activeDialog === `create` && <CreateWebhookDialog onClose={closeWebhookDialog} projectId={projectId} />}
            {activeDialog === `update` && activeWebhook && (
              <UpdateWebhookDialog webhook={activeWebhook} onClose={closeWebhookDialog} projectId={projectId} />
            )}
            {activeDialog === `test` && activeWebhook && (
              <TestWebhookDialog webhook={activeWebhook} onClose={closeWebhookDialog} />
            )}
            {activeDialog === `delete` && activeWebhook && (
              <DeleteWebhookDialog webhook={activeWebhook} onClose={closeWebhookDialog} />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

Webhooks.defaultProps = {
  webhooks: undefined,
  projects: [],
  activeProjectId: undefined,
};

export default Webhooks;
